import React, {
  useCallback, useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import paginationFactory from 'react-bootstrap-table2-paginator';

import { v4 as uuidv4 } from 'uuid';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import AuditSubTable from '@components/table/AuditSubTable';
import AuditTableSearchInputGroup from '@components/smartAdminUI/input/AuditTableSearchInputGroup';
import AgentAvatar from '@components/table/avatar/AgentAvatar';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { getAuditData } from '../../redux/selectors/AuditSelector';

const AuditTable = ({ sendFilteredDataToParent }) => {
  const auditData = useSelector(getAuditData);
  const [usingAdvancedSearch, setUsingAdvancedSearch] = useState(false);
  const [searchObject, setSearchObject] = useState({});
  const [subTransactionData, setSubTransactionData] = useState([]);
  const distinctTransactionData = useMemo(() => {
    if (isEmpty(auditData)) return [];
    if (isEmpty(searchObject)) {
      setSubTransactionData(auditData);
      return Object.values(
        auditData.flat()
          .reduce((result, obj) => {
            const {
              TransactionID,
              RecordUniqueID,
              ChangeTimeStamp,
              RealUser,
              ImpersonatedUser,
              Transactiontype,
            } = obj;
            const distinctData = result;
            distinctData[TransactionID] = distinctData[TransactionID] || {
              UniqueId: uuidv4(),
              TransactionID,
              RecordUniqueID,
              ChangeTimeStamp,
              RealUser,
              ImpersonatedUser,
              Transactiontype,
            };
            return distinctData;
          }, {}),
      );
    }

    const filteredDisplayData = auditData.flat().filter(row => {
      let searchFound;
      if (usingAdvancedSearch) {
        const searchFields = {
          transactionId: row.TransactionID.toLowerCase(),
          changedBy: row.RealUser.toLowerCase(),
          impersonating: row.ImpersonatedUser.toLowerCase(),
          transactionType: row.Transactiontype.toLowerCase(),
          fieldType: row.ClassName.toLowerCase(),
          fieldName: row.FieldName.toLowerCase(),
          changedFrom: String(row.OldValue).toLowerCase(),
          changedTo: String(row.NewValue).toLowerCase(),
        };
        searchFound = ((isEmpty(searchObject.transactionId) || searchFields.transactionId.includes(searchObject.transactionId))
          && (isEmpty(searchObject.changedBy) || searchFields.changedBy.includes(searchObject.changedBy))
          && (isEmpty(searchObject.impersonating) || searchFields.impersonating.includes(searchObject.impersonating))
          && (isEmpty(searchObject.transactionType) || searchFields.transactionType.includes(searchObject.transactionType))
          && (isEmpty(searchObject.fieldType) || searchFields.fieldType.includes(searchObject.fieldType))
          && (isEmpty(searchObject.fieldName) || searchFields.fieldName.includes(searchObject.fieldName))
          && (isEmpty(searchObject.changedFrom) || searchFields.changedFrom.includes(searchObject.changedFrom))
          && (isEmpty(searchObject.changedTo) || searchFields.changedTo.includes(searchObject.changedTo)))
          || Object.values(searchObject).every(value => isEmpty(value));
      } else {
        const searchFields = [
          row.TransactionID.toLowerCase(),
          row.ChangeTimeStamp.toLowerCase(),
          row.RealUser.toLowerCase(),
          row.ImpersonatedUser.toLowerCase(),
          row.Transactiontype.toLowerCase(),
          row.ClassName.toLowerCase(),
          row.FieldName.toLowerCase(),
          String(row.OldValue).toLowerCase(),
          String(row.NewValue).toLowerCase(),
        ];
        searchFound = searchFields.some(field => field.includes(searchObject.searchText));
      }
      return searchFound;
    });
    setSubTransactionData(filteredDisplayData);
    const distinctTransactions = (Object.values(
      filteredDisplayData.flat()
        .reduce((result, obj) => {
          const {
            TransactionID,
            RecordUniqueID,
            ChangeTimeStamp,
            RealUser,
            ImpersonatedUser,
            Transactiontype,
          } = obj;
          const distinctData = result;
          distinctData[TransactionID] = distinctData[TransactionID] || {
            UniqueId: uuidv4(),
            TransactionID,
            RecordUniqueID,
            ChangeTimeStamp,
            RealUser,
            ImpersonatedUser,
            Transactiontype,
          };
          return distinctData;
        }, {}),
    ));
    return distinctTransactions;
  }, [auditData, searchObject]);

  useEffect(() => {
    if (isEmpty(subTransactionData)) return;
    sendFilteredDataToParent(subTransactionData);
  }, [subTransactionData]);

  const hdrSortClasses = (_column, sortOrder) => `sorting_${sortOrder}`;

  const columns = [
    {
      dataField: 'TransactionID',
      text: 'ID',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
    },
    {
      dataField: 'ChangeTimeStamp',
      text: 'Date',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      sortFunc: (a, b, order) => (order === 'asc' ? Date.parse(a) - Date.parse(b) : Date.parse(b) - Date.parse(a)),
    },
    {
      dataField: 'Transactiontype',
      text: 'Type',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      headerStyle: { width: '100px' },
    },
    {
      dataField: 'RealUser',
      text: 'Changed By',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      headerStyle: { width: '100px' },
      classes: 'text-center',
      formatter: cellContent => <AgentAvatar fullName={cellContent} showFullName={false} />,
    },
    {
      dataField: 'ImpersonatedUser',
      text: 'Impersonating',
      headerSortingClasses: hdrSortClasses,
      headerAttrs: { className: 'sorting' },
      sort: true,
      headerStyle: { width: '100px' },
      classes: 'text-center',
      formatter: (cellContent, column) => (column.RealUser === cellContent ? '' : <AgentAvatar fullName={cellContent} showFullName={false} />),
    },
  ];

  const handleSearch = object => {
    setSearchObject(object);
  };

  const expandRow = useCallback(() => ({
    renderer: row => {
      const subTransactionDisplayData = subTransactionData.filter(subTransaction => subTransaction.TransactionID === row.TransactionID).map(item => ({ ...item, UniqueId: uuidv4() }));
      return (<AuditSubTable displayData={subTransactionDisplayData} />);
    },
    showExpandColumn: true,
    expandByColumnOnly: true,
    classes: 'text-center',
    expandHeaderColumnRenderer: ({ isAnyExpands }) => {
      if (isAnyExpands) {
        return (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="tooltip-collapseAll-button" style={{ margin: 0 }}>Collapse All</Tooltip>}
          >
            <button type="button" className="btn btn-sm btn-primary">
              <i className="far fa-minus" />
            </button>
          </OverlayTrigger>
        );
      }
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="tooltip-expandAll-button" style={{ margin: 0 }}>Expand All</Tooltip>}
        >
          <button type="button" className="btn btn-primary btn-sm">
            <i className="far fa-plus" />
          </button>
        </OverlayTrigger>
      );
    },
    expandColumnRenderer: ({ expanded }) => {
      if (expanded) {
        return (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="tooltip-collapse-button" style={{ margin: 0 }}>Collapse</Tooltip>}
          >
            <button type="button" className="btn btn-sm btn-primary">
              <i className="far fa-chevron-up" />
            </button>
          </OverlayTrigger>
        );
      }
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="tooltip-expand-button" style={{ margin: 0 }}>Expand</Tooltip>}
        >
          <button type="button" className="btn btn-sm btn-primary">
            <i className="far fa-chevron-down" />
          </button>
        </OverlayTrigger>
      );
    },
  }), [subTransactionData, searchObject]);

  const defaultSorted = [{
    dataField: 'ChangeTimeStamp',
    order: 'desc',
  }];

  const paginationOptions = {
    sizePerPage: 20,
    pageStartIndex: 1,
    hideSizePerPage: true, // Hide the sizePerPage dropdown always
    hidePageListOnlyOnePage: true, // Hide the pagination list when only one page
    showTotal: true,
  };

  return (
    <ToolkitProvider
      keyField="UniqueId"
      data={distinctTransactionData}
      columns={columns}
      search
    >
      { toolKit => (
        <div className="dataTables_wrapper dt-bootstrap4 no-footer">
          {
            distinctTransactionData && (
              <>
                <AuditTableSearchInputGroup
                  setUsingAdvancedSearch={setUsingAdvancedSearch}
                  onSearch={handleSearch}
                />
                <BootstrapTable
                  defaultSorted={defaultSorted}
                  expandRow={expandRow()}
                  wrapperClasses="table-responsive"
                  classes="dataTable"
                  bordered={false}
                  striped
                  hover={false}
                  id="auditTable"
                  noDataIndication="No Data Returned."
                  pagination={distinctTransactionData.length > 1 ? paginationFactory(paginationOptions) : null}
                  {...toolKit.baseProps}
                />
              </>
            )
          }
        </div>
      )}
    </ToolkitProvider>
  );
};

AuditTable.propTypes = {
  sendFilteredDataToParent: PropTypes.func.isRequired,
};

export default AuditTable;
